import React, { useState, useEffect } from 'react';
import { Eye, Send, CheckCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EmailCampaignsTable = ({ onPreview }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(''); // '' = all, 'DRAFT', 'PENDING_APPROVAL', etc.

  useEffect(() => {
    fetchCampaigns();
  }, [filter]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = `${API_URL}/api/email-campaigns`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // El endpoint devuelve un array directamente
      let allCampaigns = response.data || [];
      
      // Filtrar por status si es necesario
      if (filter) {
        allCampaigns = allCampaigns.filter(c => c.status === filter);
      }
      
      setCampaigns(allCampaigns);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Error al cargar campañas');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (campaignId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/email-campaigns/${campaignId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Campaña aprobada exitosamente');
      fetchCampaigns();
    } catch (error) {
      console.error('Error approving campaign:', error);
      toast.error(error.response?.data?.detail || 'Error al aprobar campaña');
    }
  };

  const handleSendNow = async (campaignId) => {
    if (!window.confirm('¿Estás seguro de enviar esta campaña ahora?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/email-campaigns/${campaignId}/send_now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Campaña encolada para envío');
      fetchCampaigns();
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar campaña');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      DRAFT: { label: 'Borrador', className: 'bg-gray-100 text-gray-800' },
      PENDING_APPROVAL: { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-800' },
      APPROVED: { label: 'Aprobada', className: 'bg-green-100 text-green-800' },
      SENT: { label: 'Enviada', className: 'bg-blue-100 text-blue-800' },
      CANCELED: { label: 'Cancelada', className: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig[status] || statusConfig.DRAFT;
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const canApprove = (campaign) => {
    return campaign.status === 'PENDING_APPROVAL';
  };

  const canSend = (campaign) => {
    return campaign.status === 'DRAFT' || campaign.status === 'APPROVED';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-slate-500">Cargando campañas...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={() => setFilter('')}
          variant={filter === '' ? 'default' : 'outline'}
          size="sm"
        >
          Todas
        </Button>
        <Button
          onClick={() => setFilter('DRAFT')}
          variant={filter === 'DRAFT' ? 'default' : 'outline'}
          size="sm"
        >
          Borradores
        </Button>
        <Button
          onClick={() => setFilter('PENDING_APPROVAL')}
          variant={filter === 'PENDING_APPROVAL' ? 'default' : 'outline'}
          size="sm"
        >
          Pendientes
        </Button>
        <Button
          onClick={() => setFilter('APPROVED')}
          variant={filter === 'APPROVED' ? 'default' : 'outline'}
          size="sm"
        >
          Aprobadas
        </Button>
        <Button
          onClick={() => setFilter('SENT')}
          variant={filter === 'SENT' ? 'default' : 'outline'}
          size="sm"
        >
          Enviadas
        </Button>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-slate-200 rounded-lg">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Campaña
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Tipo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Creada
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {campaigns.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-slate-500">
                  No hay campañas disponibles
                </td>
              </tr>
            ) : (
              campaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-900">
                      {campaign.name}
                    </div>
                    <div className="text-sm text-slate-500">
                      {campaign.subject}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {campaign.campaign_type}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(campaign.status || 'DRAFT')}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {new Date(campaign.created_at).toLocaleDateString('es-ES')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      {/* Botón Preview - siempre disponible */}
                      <Button
                        onClick={() => onPreview(campaign)}
                        variant="outline"
                        size="sm"
                        title="Previsualizar"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>

                      {/* Botón Aprobar - solo si está PENDING_APPROVAL */}
                      {canApprove(campaign) && (
                        <Button
                          onClick={() => handleApprove(campaign.id)}
                          variant="outline"
                          size="sm"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          title="Aprobar"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      )}

                      {/* Botón Enviar Ahora - solo si es DRAFT o APPROVED */}
                      {canSend(campaign) && (
                        <Button
                          onClick={() => handleSendNow(campaign.id)}
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Enviar Ahora"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
